import { useMemo } from "react";
import { useGetAdminGroupHierarchy } from "hooks/useAdminGroup/useGetAdminGroupHierarchy";
import AppData from "app_data";

export interface HierarchyNode {
  id?: string | null;
  type?: string | null;
  externalId?: string | number | null;
  children?: HierarchyNode[];
}

export interface UseAdminGroupSubtreeIdsResult {
  /**
   * externalIds of every node in the selected admin group's subtree -- the
   * group itself, its descendant ADMIN_GROUPs, and descendant LOCATIONs --
   * normalized to strings (location externalIds may be numeric or string,
   * admin-group externalIds are strings). The selected group's own
   * externalId is included so a leaf admin group (no children) still scopes
   * to itself instead of resolving to an empty scope.
   */
  subtreeExternalIds: string[];
  /**
   * False while the hierarchy is loading. A failed load keeps this false,
   * which is safe: useScopedWayfinderSelection resolves a failed hierarchy
   * to no scope, so no caller waits on this hook in that state.
   */
  isReady: boolean;
}

const findNodeById = (
  node: HierarchyNode | undefined,
  targetId: string,
): HierarchyNode | null => {
  if (!node) return null;
  if (node.id && node.id === targetId) return node;
  if (!Array.isArray(node.children)) return null;
  for (const child of node.children) {
    const found = findNodeById(child, targetId);
    if (found) return found;
  }
  return null;
};

export const collectSubtreeExternalIds = (
  node: HierarchyNode | undefined,
  out: Set<string>,
): void => {
  if (!node) return;

  // Every node type contributes its externalId (ADMIN_GROUP and LOCATION
  // alike); nodes without one contribute nothing. LOCATION nodes are
  // terminal, but the generic children check handles both shapes safely.
  const externalId = String(node.externalId ?? "").trim();
  if (externalId) out.add(externalId);

  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      collectSubtreeExternalIds(child, out);
    }
  }
};

// Walks the admin-group subtree from `adminGroupId` (the node itself plus all
// descendants), collecting ADMIN_GROUP and LOCATION externalIds into a single
// deduped list. Reuses useGetAdminGroupHierarchy's own react-query cache
// (key ["adminGroupHierarchy", corpAccountNbr]) -- no extra fetch.
export const useAdminGroupSubtreeIds = (
  adminGroupId?: string,
): UseAdminGroupSubtreeIdsResult => {
  const currentCorp: string | number | undefined = AppData.user?.enterprise_id;

  const { data: hierarchy } = useGetAdminGroupHierarchy({
    corpAccountNbr: String(currentCorp ?? ""),
    isLbaEnabled: Boolean(adminGroupId),
  });

  return useMemo<UseAdminGroupSubtreeIdsResult>(() => {
    if (!adminGroupId || !hierarchy) {
      return { subtreeExternalIds: [], isReady: false };
    }

    const matched = findNodeById(hierarchy as HierarchyNode, adminGroupId);
    if (!matched) {
      return { subtreeExternalIds: [], isReady: true };
    }

    const ids = new Set<string>();
    collectSubtreeExternalIds(matched, ids);
    return { subtreeExternalIds: Array.from(ids), isReady: true };
  }, [adminGroupId, hierarchy]);
};
